import { ToastrService } from 'ngx-toastr';
import pluralize from 'pluralize';
import { Observable, of, throwError } from 'rxjs';
import { map, switchMap, tap } from 'rxjs/operators';

import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AbstractControl, AsyncValidatorFn } from '@angular/forms';

import { ISnListResponse, ISnListQueryParameters, ISnFeedQueryParameters } from '@lib/api';
import { SnTeam } from '@lib/api/entities';
import { SnDialogService } from '@lib/ui/dialog';

@Injectable({ providedIn: 'root' })
export class SnTeamsAPI {
  constructor(
    private readonly _httpClient: HttpClient,
    private readonly _toastrService: ToastrService,
    private readonly _dialogService: SnDialogService,
  ) {}

  public getTeamList(params?: ISnListQueryParameters): Observable<ISnListResponse<SnTeam>> {
    return this._httpClient.get('/api/teams/', { params }).pipe(
      map((response: any) => ({
        count: response.count,
        totalPages: response.totalPages,
        results: response.results.map((team) => new SnTeam(team)),
      })),
    );
  }

  public feedTeams(params: ISnFeedQueryParameters): Observable<SnTeam[]> {
    return this._httpClient
      .get('/api/teams/feed/', { params })
      .pipe(map((response: any) => response.map((user) => new SnTeam(user))));
  }

  public getRawTeam(id: string): Observable<Object> {
    return this._httpClient.get(`/api/teams/${id}/`);
  }

  public getTeam(id: string): Observable<SnTeam> {
    return this.getRawTeam(id).pipe(map((response: any) => new SnTeam(response)));
  }

  public createTeam(data: any): Observable<SnTeam> {
    return this._httpClient.post('/api/teams/', data).pipe(map((response) => new SnTeam(response)));
  }

  public updateTeam(id: string, data: any): Observable<SnTeam> {
    return this._httpClient.patch(`/api/teams/${id}/`, data).pipe(
      map((response) => new SnTeam(response)),
      tap({
        next: () => this._toastrService.success('Team updated'),
        error: () => this._toastrService.error('Could not update team. Please try again later'),
      }),
    );
  }

  public deleteTeam(id: string): Observable<Object> {
    return this._dialogService
      .confirm({
        message: 'users.teams.areYouSureDeleteTeam',
        confirmButton: {
          text: 'common.delete',
          color: 'warn',
        },
      })
      .pipe(
        switchMap(() => this._httpClient.delete(`/api/teams/${id}/`)),
        tap({
          next: () => this._toastrService.success('Team deleted'),
          error: () =>
            this._toastrService.error('Team cannot be deleted. You may not have proper permission'),
        }),
      );
  }

  public importTeams(file: File): Observable<{ created: any; updated: any }> {
    const formData = new FormData();
    formData.append('file', file);

    return this._httpClient.post('/api/teams/import/from-csv/', formData).pipe(
      tap({
        error: () => this._toastrService.error('Could not import teams. Please try again later'),
      }),
      switchMap((response: any) => {
        const { created, updated, errors } = response;

        if (created) {
          this._toastrService.success(`${created} ${pluralize('team', created)} created`);
        }

        if (updated) {
          this._toastrService.warning(`${updated} ${pluralize('team', updated)} updated`);
        }

        if (errors) {
          return throwError(errors);
        }

        return of({ created, updated });
      }),
    );
  }

  public validateUniqueName(exceptionName?: string): AsyncValidatorFn {
    return (control: AbstractControl) => {
      if (!control.value) {
        return of(null);
      }

      if (exceptionName && control.value === exceptionName) {
        return of(null);
      }

      return this.getTeamList({ name: control.value }).pipe(
        map(({ count }) => (count ? { unique: true } : null)),
      );
    };
  }
}
